import { Button, Select, TextAreaInput, TextInput } from '@octano/global-ui';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import AzureFilesUploader from '../../../../components/AzureFilesUploader/AzureFilesUploader';
import { useValidations } from '../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../locales/translations';
import {
  StudentsRemovalRequestCreateFormFields,
  StudentsRemovalRequestCreateRequest,
  StudentsRemovalRequestFormFile,
} from '../types/students';
import { convertToFormFile } from '../utils';
import { StudentsRemovalRequestFormFileChip } from './StudentsRemovalRequestFormFileChip';

const DEFAULT_VALUES: StudentsRemovalRequestCreateFormFields = {
  type: null,
  comment: '',
  files: [],
};

export function StudentsRemovalRequestForm({
  studyPlanVersionName,
  types,
  onSubmit,
  onCancel,
}: {
  studyPlanVersionName: string;
  types: { id: string; name: string }[];
  onSubmit: (payload: StudentsRemovalRequestCreateRequest) => Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_MANAGEMENT_STUDENT_REMOVAL_REQUEST_STUDENTS,
  );
  const texts = useMemo(
    () => ({
      studyPlanVersionName: t('students.form.studyPlanVersionName'),
      comment: t('students.form.comment'),
      type: t('students.form.type'),
      files: t('students.form.files'),
    }),
    [t],
  );
  const { msgValidations } = useValidations();

  const [filesState, setFilesState] = useState<number>(0);

  const form = useForm<StudentsRemovalRequestCreateFormFields>({
    defaultValues: DEFAULT_VALUES,
  });
  const { control, watch } = form;
  const supplementaryFiles = useFieldArray({
    control,
    name: 'files',
  });

  const typeOptions = useMemo(() => {
    return types.map(({ id, name }) => ({ label: name, value: id }));
  }, [types]);

  const files = watch('files');

  const handleAddFile = useCallback(
    (file: StudentsRemovalRequestFormFile) => {
      supplementaryFiles.append(file);
      setFilesState((prev) => prev + 1);
    },
    [supplementaryFiles],
  );
  const handleRemoveFile = useCallback(
    (index: number) => {
      supplementaryFiles.remove(index);
      setFilesState((prev) => prev + 1);
    },
    [supplementaryFiles],
  );

  const handleSubmit = useCallback(
    async (values: StudentsRemovalRequestCreateFormFields) => {
      if (!values.type) {
        return;
      }
      const payload: StudentsRemovalRequestCreateRequest = {
        typeId: values.type.value as number,
        comment: values.comment,
        filesIds: values?.files?.map((e) => e.id) || [],
      };
      await onSubmit(payload);
    },
    [onSubmit],
  );

  return (
    <FormProvider {...form}>
      <Row>
        <Col className="mb-2" xs={12}>
          <TextInput
            name="studyPlanVersionName"
            label={texts.studyPlanVersionName}
            value={studyPlanVersionName}
            rules={{ required: msgValidations.required }}
            readOnly
          />
        </Col>
        <Col className="mb-2" xs={12}>
          <Select
            control={control}
            name="type"
            label={texts.type}
            options={typeOptions}
            rules={{ required: msgValidations.required }}
          />
        </Col>
        <Col className="mb-2" xs={12}>
          <TextAreaInput
            control={control}
            name="comment"
            label={texts.comment}
            rules={{ maxLength: msgValidations.invalidMaxLength(500) }}
          />
        </Col>
        <Col className="mt-3" xs={12}>
          <div
            className="w-full p-4 rounded"
            style={{
              boxShadow: '0px 0px 10px 2px rgba(17,17,26,0.08)',
            }}
            key={filesState.toString()}
          >
            <AzureFilesUploader
              label={texts.files}
              onUploaded={(next) => handleAddFile(convertToFormFile(next))}
            />
            {!!files?.length && (
              <div
                className="w-full mt-3 d-flex flex-wrap align-items-center justify-content-start"
                style={{ gap: 5 }}
              >
                {files?.map((file, i) => (
                  <StudentsRemovalRequestFormFileChip
                    key={file.id.toString()}
                    value={i}
                    children={file.name}
                    onRemove={handleRemoveFile}
                  />
                ))}
              </div>
            )}
          </div>
        </Col>
      </Row>
      <Row className="pt-5">
        <Col xs={12} lg={6} className="pb-2 order-2 order-lg-1">
          <Button
            type="button"
            outlined
            disabled={form.formState.isSubmitting}
            onClick={onCancel}
            text={t(`common:actions.cancel`)}
            fullwidth
          />
        </Col>
        <Col xs={12} lg={6} className="pb-2 order-1 order-lg-2">
          <Button
            type="button"
            loading={form.formState.isSubmitting}
            text={t(`common:actions.request`)}
            onClick={form.handleSubmit(handleSubmit)}
            fullwidth
          />
        </Col>
      </Row>
    </FormProvider>
  );
}
